import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaChevronDown, FaChevronRight } from "react-icons/fa";
import logo from "../../assets/mmpsrpc.svg";
import svkm from "../../assets/svkm.png";
import ksv from "../../assets/ksv.png";
import srlLogo from "../../assets/SRL.png";
import StarBorder from "./StarBorder";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRefs = useRef([]);
  const headerRef = useRef(null);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (Math.abs(st - lastScrollTop.current) > 5) {
        setIsScrolled(st > 50);
        lastScrollTop.current = st <= 0 ? 0 : st;
      }
    };

    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        headerRef.current &&
        !headerRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
      if (
        activeDropdown !== null &&
        !dropdownRefs.current[activeDropdown]?.contains(event.target)
      ) {
        setActiveDropdown(null);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen, activeDropdown]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navItems = [
    {
      name: "Apply for Research",
      link: "https://drive.google.com/drive/folders/12-MUMb6cEoOsR2LzZ4IqgiILBsuNlAEq",
      external: true,
    },
    {
      name: "Activities",
      items: [
        { name: "Hackathons", link: "/hackathons" },
        { name: "Projects", link: "/projects" },
        { name: "Achievements", link: "/achievements" },
        { name: "Events", link: "/events" },
      ],
    },
    {
      name: "Media",
      items: [
        { name: "Gallery", link: "/gallery" },
        { name: "News", link: "/news" },
      ],
    },
    {
      name: "IEEE",
      link: "/ieee",
    },
    {
      name: "Coordinators",
      link: "/faculties",
    },
    {
      name: "About Us",
      items: [
        {
          name: "MMPSRPC",
          items: [
            { name: "About MMPSRPC", link: "/aboutus" },
            { name: "Mission", link: "/about/mission" },
            { name: "Vision", link: "/about/vision" },
            { name: "Goals", link: "/about/goals" },
          ],
        },
        { name: "KSV", link: "/ksv" },
        { name: "SVKM", link: "/svkm" },
        { name: "Contact Us", link: "/contactus" },
      ],
    },
    {
      name: "SRL",
      link: "https://srl.mmpsrpc.in/",
      external: true,
      isSpecial: true,
    }
    
  ];

  const handleDropdownToggle = (index, e) => {
    e.preventDefault();
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleLinkClick = (link) => {
    setIsMenuOpen(false);
    navigate(link);
  };

  const DesktopNavItem = ({ item, index, level = 0 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    if (item.items) {
      return (
        <div
          className="relative group "
          ref={dropdownRef}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <button
            onClick={(e) => handleDropdownToggle(index, e)}
            className={`flex items-center font-medium transition-all duration-300 px-2 xl:px-3 py-2 rounded-md whitespace-nowrap ${
              level === 0
                ? location.pathname.startsWith(item.link)
                  ? "bg-white text-teal-800 text-xs lg:text-xs xl:text-sm"
                  : "text-white hover:bg-teal-600 text-xs lg:text-xs xl:text-sm"
                : "w-full text-left text-gray-800 hover:bg-teal-50 hover:text-teal-800 text-xs"
            }`}
          >
            <span className="truncate">{item.name}</span>
            {level === 0 ? (
              <FaChevronDown
                className={`ml-1 flex-shrink-0 text-xs ${
                  isOpen ? "transform rotate-180" : ""
                }`}
              />
            ) : (
              <FaChevronRight className="ml-auto flex-shrink-0 text-xs" />
            )}
          </button>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{
                  opacity: 0,
                  x: level === 0 ? 0 : -10,
                  y: level === 0 ? -10 : 0,
                }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                exit={{
                  opacity: 0,
                  x: level === 0 ? 0 : -10,
                  y: level === 0 ? -10 : 0,
                }}
                transition={{ duration: 0.2 }}
                className={`absolute ${
                  level === 0 ? "left-0 mt-2" : "left-full top-0 ml-1"
                } w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}
              >
                <div className="py-1">
                  {item.items.map((subItem, subIndex) => (
                    <DesktopNavItem
                      key={subItem.name}
                      item={subItem}
                      index={`${index}-${subIndex}`}
                      level={level + 1}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    } else {
      return item.external ? (
        item.isSpecial ? (
          <StarBorder
            as="a"
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-[40px]"
            color="#fae9bd"
            speed="6s"
            thickness={2}
          >
            <img src={srlLogo} alt="SRL" className="h-12 w-auto" />
            <span>{item.name}</span>
          </StarBorder>
        ) : (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`block px-2 xl:px-4 py-2 font-medium transition-all duration-300 rounded-md whitespace-nowrap ${
              level === 0
              ? "text-white hover:bg-teal-600 text-xs lg:text-xs xl:text-sm"
              : "text-gray-800 hover:bg-teal-50 hover:text-teal-800 text-xs"
            }`}
          >
            <span className="truncate">{item.name}</span>
          </a>
        )
      ) : (
        <Link
          to={item.link}
          className={`block px-2 xl:px-4 py-2 font-medium transition-all duration-300 rounded-md whitespace-nowrap ${
            level === 0
              ? location.pathname === item.link
                ? "bg-white text-teal-800 text-xs lg:text-xs xl:text-sm"
                : "text-white hover:bg-teal-600 text-xs lg:text-xs xl:text-sm"
              : location.pathname === item.link
              ? "bg-teal-100 text-teal-800 text-xs"
              : "text-gray-800 hover:bg-teal-50 hover:text-teal-800 text-xs"
          }`}
          onClick={() => handleLinkClick(item.link)}
        >
          <span className="truncate">{item.name}</span>
        </Link>
      );
    }
  };

  const MobileNavItem = ({ item, level = 0 }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (item.items) {
      return (
        <>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`flex justify-between items-center w-full py-2 px-4 rounded-md transition duration-300 ${
              isOpen
                ? "bg-teal-600 text-white"
                : "text-gray-200 hover:bg-teal-600 hover:text-white"
            }`}
            style={{ paddingLeft: `${level * 1 + 1}rem` }}
          >
            {item.name}
            <FaChevronDown
              className={`ml-2 transform transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          <AnimatePresence>
            {isOpen && (
              <motion.ul
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="mt-1 space-y-1 bg-teal-700 rounded-md"
              >
                {item.items.map((subItem) => (
                  <li key={subItem.name}>
                    <MobileNavItem item={subItem} level={level + 1} />
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </>
      );
    } else {
      return item.external ? (
        item.isSpecial ? (
          <StarBorder
            as="a"
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block mx-4 mt-2 inline-flex items-center gap-2"
            color="#fae9bd"
            speed="6s"
            thickness={2}
          >
            <img src={srlLogo} alt="SRL" className="h-14 w-auto" />
            <span>{item.name}</span>
          </StarBorder>
        ) : (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`block py-2 px-4 rounded-md transition duration-300 text-gray-200 hover:bg-teal-600 hover:text-white`}
            style={{ paddingLeft: `${level * 1 + 1}rem` }}
          >
            {item.name}
          </a>
        )
      ) : (
        <Link
          to={item.link}
          className={`block py-2 px-4 rounded-md transition duration-300 ${
            location.pathname === item.link
              ? "bg-white text-teal-800"
              : "text-gray-200 hover:bg-teal-600 hover:text-white"
          }`}
          style={{ paddingLeft: `${level * 1 + 1}rem` }}
          onClick={() => handleLinkClick(item.link)}
        >
          {item.name}
        </Link>
      );
    }
  };

  return (
    <header
      ref={headerRef}
      className={`bg-gradient-to-r from-teal-800 via-teal-700 to-teal-600 shadow-lg sticky top-0 z-40 transition-all duration-300 ${
        isScrolled ? "py-2" : "py-3 lg:py-4"
      }`}
    >
      <style>{`
        .glow-button {
          background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
          position: relative;
          overflow: hidden;
          z-index: 1;
          box-shadow: 0 10px 22px rgba(20, 184, 166, 0.2);
          transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
          color: #14b8a6;
          font-weight: 600;
          letter-spacing: 0.5px;
          border: 1px solid rgba(20, 184, 166, 0.3);
        }

        .glow-button:hover {
          transform: scale(1.08);
          box-shadow: 0 15px 40px rgba(20, 184, 166, 0.5);
          background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
          color: #0d9488;
          border-color: rgba(20, 184, 166, 0.6);
        }

        .glow-button-mobile {
          position: relative;
          overflow: hidden;
          z-index: 1;
          box-shadow: 0 8px 18px rgba(20, 184, 166, 0.2);
          background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
          border: 1px solid rgba(20, 184, 166, 0.3);
          color: #14b8a6;
          font-weight: 600;
          transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
        }

        .glow-button-mobile:hover {
          transform: scale(1.05);
          box-shadow: 0 12px 25px rgba(20, 184, 166, 0.5);
          background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
          color: #0d9488;
          border-color: rgba(20, 184, 166, 0.6);
        }
      `}</style>
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "url(https://www.transparenttextures.com/patterns/cubes.png)",
        }}
      ></div>

      <div className=" w-[98%] mx-auto  relative z-10">
        <div className="flex justify-between items-center">
          {/* Logo and Title Section */}
          <Link to="/" className="flex items-center flex-shrink-0 min-w-0">
            <div className="w-12 h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 bg-white rounded-full flex items-center justify-center mr-2 lg:mr-3 flex-shrink-0">
              <img
                src={logo}
                alt="MMPSRPC Logo"
                className="w-10 h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-bold text-white hover:text-beige-300 transition duration-300 leading-tight">
                <span className="xl:hidden text-sm lg:text-xs">MMPSRPC</span>
                <span className="hidden xl:inline text-sm xl:text-base 2xl:text-lg">
                  M. M. Patel Students Research Project Cell
                </span>
              </h1>
              <p className="text-[#F8E7C1] font-semibold leading-tight text-xs lg:text-xs xl:text-sm">
                Kadi Sarva Vishwavidyalaya
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center justify-center flex-1 mx-2">
            <div className="flex items-center text-sm">
              {navItems.map((item, index) => (
                <DesktopNavItem key={item.name} item={item} index={index} />
              ))}
            </div>
          </nav>

          {/* External Links Section */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-2">
            <a
              href="https://www.ksv.ac.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <img
                src={ksv}
                alt="KSV Logo"
                className="w-10 h-10 lg:w-10 lg:h-10 xl:w-14 xl:h-14 transform hover:scale-105 transition-transform duration-300"
              />
            </a>
            <a
              href="https://www.svkm.org.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <img
                src={svkm}
                alt="SVKM Logo"
                className="w-8 h-10 lg:w-8 lg:h-11 xl:w-11 xl:h-14 transform hover:scale-105 transition-transform duration-300"
              />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white hover:text-beige-300 transition duration-300 flex-shrink-0"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {/* Mobile Navigation*/}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden mt-4"
            >
              <nav>
                <ul className="flex flex-col space-y-2">
                  {navItems.map((item) => (
                    <li key={item.name}>
                      <MobileNavItem item={item} />
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="flex justify-center items-center space-x-4 mt-6">
                <a
                  href="https://www.ksv.ac.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={ksv}
                    alt="KSV Logo"
                    className="w-12 h-12 transform hover:scale-105 transition-transform duration-300"
                  />
                </a>
                <a
                  href="https://www.svkm.org.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={svkm}
                    alt="SVKM Logo"
                    className="w-11 h-12 transform hover:scale-105 transition-transform duration-300"
                  />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
